import {
  ComplianceFile,
  DocumentLink,
  DocumentLinkType,
  DocumentShare,
  DocumentShareMode,
  DraftKind,
  EmissionBookEntry,
  Person,
  PolicyDocument,
  PolicyDocumentKind,
  ProtoDb,
  TreeMember,
  UnusualReport,
  WizardDraft,
} from '../models/domain.models';
import { DOCUMENT_KIND_LABELS } from '../models/labels';
import { DOCUMENT_SHARE_VALID_DAYS } from '../models/rules';
import { PublicDocuments } from '../models/public.models';
import {
  ComplianceFileView,
  DocumentShareRow,
  DraftRow,
  EmissionBookView,
  EmissionOperationRow,
  EntityOption,
  PolicyExtras,
} from '../models/views.models';
import { holderOf, subtreeOids } from './commission-math';
import { isAssociated } from './document-links';
import { failure, Outcome } from './outcome';
import { policyExtras, sumInsuredOf } from './policy-extras';
import { addDays } from './rng';
import { MEMBERSHIPS } from './seed-named';
import { StoreHost } from './store-host';

export interface UploadDocumentBody {
  name: string;
  kind: PolicyDocumentKind;
  links: DocumentLink[];
}

export interface ShareDocumentsBody {
  mode: DocumentShareMode;
  personOid: string;
  documentOids?: string[];
  items?: string[];
  message?: string;
}

export interface LegajoFileBody {
  activity: string;
  fundsOrigin: string;
  pep: boolean;
  pepRole: string;
  obligedSubjectDeclared: boolean;
  attachments: string[];
}

export interface UnusualReportBody {
  personOid: string;
  policyOids: string[];
  operationDate: string;
  description: string;
  attachments: string[];
}

export interface DraftBody {
  oid?: string;
  kind: DraftKind;
  step: number;
  label: string;
  personOid?: string | null;
  data: Record<string, unknown>;
}

const LINK_TYPES: DocumentLinkType[] = ['policy', 'claim', 'endorsement', 'ticket', 'person'];

export function emissionOperations(db: ProtoDb, nodeOids: Set<string>, from: string, to: string): EmissionOperationRow[] {
  const members = new Map(db.members.map(m => [m.oid, m]));
  const treeName = (oid: string) => db.trees.find(t => t.oid === members.get(oid)?.treeOid)?.name ?? '—';
  const persons = new Map(db.persons.map(p => [p.oid, p.displayName]));
  const companies = new Map(db.companies.map(c => [c.oid, c.name]));
  return db.policies
    .filter(p => nodeOids.has(p.licenseHolderOid ?? holderOf(db.members, p.ownerOid)) && p.startDate >= from && p.startDate <= to)
    .map(p => ({
      policyOid: p.oid, date: p.startDate, number: p.number, companyName: companies.get(p.companyOid) ?? '—', line: p.lineOfBusiness,
      insuredName: persons.get(p.personOid) ?? '—', sumInsured: sumInsuredOf(p), premium: p.premium, treeName: treeName(p.ownerOid),
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number));
}

/** P-70 / E-02 documents, P-23 legajo, P-87 reports, P-86 emission book and wizard drafts (D-032-fe). */
export class ComplianceEngine {
  constructor(private readonly host: StoreHost) {}

  private get db() {
    return this.host.db;
  }

  private me(): TreeMember | null {
    const oid = this.host.user()?.memberOid;
    return this.db.members.find(m => m.oid === oid) ?? null;
  }

  private owners(): Set<string> | null {
    const me = this.me();
    if (!me) return null;
    return this.host.user()?.role === 'seller' ? new Set([me.oid]) : subtreeOids(this.db.members, me.oid);
  }

  private inScope(ownerOid: string): boolean {
    return this.owners()?.has(ownerOid) ?? false;
  }

  private isProducer(): boolean {
    return this.host.user()?.role === 'producer';
  }

  private person(oid: string): Person | undefined {
    const found = this.db.persons.find(p => p.oid === oid);
    return found && this.inScope(found.ownerOid) ? found : undefined;
  }

  private resolve(link: DocumentLink): { ownerOid: string; personOid: string } | null {
    const db = this.db;
    switch (link.type) {
      case 'policy': {
        const policy = db.policies.find(p => p.oid === link.oid);
        return policy && this.inScope(policy.ownerOid) ? { ownerOid: policy.ownerOid, personOid: policy.personOid } : null;
      }
      case 'claim': {
        const claim = db.claims.find(c => c.oid === link.oid);
        return claim && this.inScope(claim.ownerOid) ? { ownerOid: claim.ownerOid, personOid: claim.personOid } : null;
      }
      case 'endorsement': {
        const endorsement = db.endorsements.find(e => e.oid === link.oid);
        const policy = db.policies.find(p => p.oid === endorsement?.policyOid);
        return endorsement && policy && this.inScope(endorsement.ownerOid)
          ? { ownerOid: endorsement.ownerOid, personOid: policy.personOid } : null;
      }
      case 'ticket': {
        const ticket = db.tickets.find(t => t.oid === link.oid);
        return ticket && this.inScope(ticket.ownerOid) ? { ownerOid: ticket.ownerOid, personOid: ticket.personOid ?? '' } : null;
      }
      case 'person': {
        const person = this.person(link.oid);
        return person ? { ownerOid: person.ownerOid, personOid: person.oid } : null;
      }
    }
  }

  // ── P-70: documents ───────────────────────────────────────────

  entityOptions(type: DocumentLinkType, filter: string): EntityOption[] {
    const owners = this.owners();
    if (!owners || !LINK_TYPES.includes(type)) return [];
    const db = this.db;
    const text = filter.trim().toLowerCase();
    const persons = new Map(db.persons.map(p => [p.oid, p.displayName]));
    const all: EntityOption[] = (() => {
      switch (type) {
        case 'policy':
          return db.policies.filter(p => owners.has(p.ownerOid)).map(p => ({
            type, oid: p.oid, label: `Póliza ${p.number}`, sub: `${persons.get(p.personOid) ?? ''} · ${p.subject}`,
          }));
        case 'claim':
          return db.claims.filter(c => owners.has(c.ownerOid)).map(c => ({
            type, oid: c.oid, label: `Siniestro ${c.number}`, sub: `${persons.get(c.personOid) ?? ''} · ${c.type}`,
          }));
        case 'endorsement':
          return db.endorsements.filter(e => owners.has(e.ownerOid)).map(e => ({
            type, oid: e.oid, label: `Endoso ${e.number}`, sub: db.policies.find(p => p.oid === e.policyOid)?.number ?? '',
          }));
        case 'ticket':
          return db.tickets.filter(t => owners.has(t.ownerOid) && t.category === 'task').map(t => ({
            type, oid: t.oid, label: `Ticket ${t.number}`, sub: t.title,
          }));
        case 'person':
          return db.persons.filter(p => owners.has(p.ownerOid)).map(p => ({
            type, oid: p.oid, label: p.displayName, sub: `${p.documentType} ${p.documentNumber}`,
          }));
      }
    })();
    return all.filter(o => !text || `${o.label} ${o.sub}`.toLowerCase().includes(text)).slice(0, 8);
  }

  uploadDocument(body: UploadDocumentBody): Outcome<PolicyDocument> {
    const me = this.me();
    if (!me) return failure(403, 'FORBIDDEN', 'Sin sesión');
    if (!body.name?.trim()) return failure(400, 'DOCUMENT_INVALID', 'Elegí un archivo');
    if (!(body.kind in DOCUMENT_KIND_LABELS)) return failure(400, 'DOCUMENT_INVALID', 'Elegí el tipo de documento');
    const links: DocumentLink[] = [];
    for (const link of body.links ?? []) {
      if (!links.some(l => l.type === link.type && l.oid === link.oid)) links.push({ type: link.type, oid: link.oid });
    }
    if (links.length === 0) return failure(400, 'DOCUMENT_INVALID', 'Asociá el documento al menos a una entidad');
    const resolved = links.map(l => this.resolve(l));
    if (resolved.some(r => r === null)) return failure(404, 'NOT_FOUND', 'Una de las entidades no existe o no está dentro de tu alcance');
    const first = resolved[0]!;
    const personLink = links.find(l => l.type === 'person');
    const document: PolicyDocument = {
      oid: `doc-${this.host.nextSequence()}`, name: body.name.trim(), kind: body.kind, origin: 'uploaded', date: this.host.today(),
      ownerOid: first.ownerOid, personOid: personLink?.oid ?? resolved.find(r => r?.personOid)?.personOid ?? '',
      policyOid: null, claimOid: null, uploadedByOid: me.oid, links,
    };
    this.db.documents.unshift(document);
    return { value: document };
  }

  associateDocument(oid: string, link: DocumentLink): Outcome<PolicyDocument> {
    const document = this.db.documents.find(d => d.oid === oid);
    if (!document || !this.inScope(document.ownerOid)) return failure(404, 'NOT_FOUND', 'Documento inexistente');
    if (!LINK_TYPES.includes(link.type) || this.resolve(link) === null) {
      return failure(404, 'NOT_FOUND', 'La entidad no existe o no está dentro de tu alcance');
    }
    if (isAssociated(document, this.db, link.type, link.oid)) return failure(409, 'ALREADY_ASSOCIATED', 'El documento ya está asociado a esa entidad');
    document.links = [...document.links, { type: link.type, oid: link.oid }];
    if (link.type === 'person' && !document.personOid) document.personOid = link.oid;
    return { value: document };
  }

  // ── P-70 → E-02: links for the end client ─────────────────────

  createShare(body: ShareDocumentsBody): Outcome<DocumentShareRow> {
    const me = this.me();
    if (!me) return failure(403, 'FORBIDDEN', 'Sin sesión');
    const person = this.person(body.personOid);
    if (!person) return failure(404, 'NOT_FOUND', 'Cliente inexistente');
    const documentOids = [...new Set(body.documentOids ?? [])];
    const items = (body.items ?? []).map(i => i.trim()).filter(Boolean);
    if (body.mode === 'download') {
      if (documentOids.length === 0) return failure(400, 'SHARE_INVALID', 'Elegí al menos un documento para compartir');
      const docs = documentOids.map(oid => this.db.documents.find(d => d.oid === oid));
      if (docs.some(d => !d || !this.inScope(d.ownerOid) || d.personOid !== person.oid)) {
        return failure(400, 'SHARE_INVALID', 'Solo se pueden compartir documentos de este cliente');
      }
    } else if (items.length === 0) {
      return failure(400, 'SHARE_INVALID', 'Indicá qué documentos le pedís al cliente');
    }
    const share: DocumentShare = {
      token: crypto.randomUUID().replace(/-/g, '').slice(0, 20), ownerOid: me.oid, mode: body.mode, personOid: person.oid,
      documentOids: body.mode === 'download' ? documentOids : [], items: body.mode === 'request' ? items : [],
      message: body.message?.trim() ?? '', createDate: this.host.today(), expiryDate: addDays(this.host.today(), DOCUMENT_SHARE_VALID_DAYS),
    };
    this.db.documentShares.unshift(share);
    return { value: this.shareRow(share) };
  }

  shares(): DocumentShareRow[] {
    return this.db.documentShares.filter(s => this.inScope(s.ownerOid)).map(s => this.shareRow(s));
  }

  private shareRow(share: DocumentShare): DocumentShareRow {
    return {
      token: share.token, mode: share.mode, personName: this.db.persons.find(p => p.oid === share.personOid)?.displayName ?? '—',
      createDate: share.createDate, expiryDate: share.expiryDate, expired: share.expiryDate < this.host.today(),
      count: share.mode === 'download' ? share.documentOids.length : share.items.length,
    };
  }

  publicDocuments(token: string): PublicDocuments | null {
    const share = this.db.documentShares.find(s => s.token === token);
    if (!share) return null;
    const owner = this.db.members.find(m => m.oid === share.ownerOid);
    const producer = (owner?.kind === 'seller' ? this.db.members.find(m => m.oid === owner.parentOid) : owner) ?? owner;
    const expired = share.expiryDate < this.host.today();
    return {
      expired, today: this.host.today(), validUntil: share.expiryDate,
      producer: {
        brand: this.db.trees.find(t => t.oid === owner?.treeOid)?.name ?? '', name: producer?.name ?? '—',
        license: producer?.license ?? null, email: producer?.email ?? '', phone: producer?.phone ?? null,
      },
      clientName: this.db.persons.find(p => p.oid === share.personOid)?.displayName ?? '',
      mode: share.mode, message: share.message,
      documents: expired ? [] : share.documentOids
        .map(oid => this.db.documents.find(d => d.oid === oid))
        .filter((d): d is PolicyDocument => !!d)
        .map(d => ({ name: d.name, kind: d.kind, date: d.date })),
      items: expired ? [] : share.items,
    };
  }

  // ── P-23: legajo ──────────────────────────────────────────────

  legajoView(personOid: string): Outcome<ComplianceFileView> {
    const person = this.person(personOid);
    if (!person) return failure(404, 'NOT_FOUND', 'Persona inexistente');
    return { value: this.legajoOf(person) };
  }

  saveLegajo(personOid: string, body: LegajoFileBody): Outcome<ComplianceFileView> {
    const person = this.person(personOid);
    if (!person) return failure(404, 'NOT_FOUND', 'Persona inexistente');
    const complete = !!body.activity.trim() && !!body.fundsOrigin.trim() && body.obligedSubjectDeclared
      && (!body.pep || !!body.pepRole.trim());
    const file: ComplianceFile = {
      personOid, ownerOid: person.ownerOid, state: complete ? 'complete' : 'incomplete', updateDate: this.host.today(),
      activity: body.activity.trim(), fundsOrigin: body.fundsOrigin.trim(), pep: body.pep, pepRole: body.pep ? body.pepRole.trim() : '',
      obligedSubjectDeclared: body.obligedSubjectDeclared, attachments: [...body.attachments],
    };
    const index = this.db.complianceFiles.findIndex(f => f.personOid === personOid);
    if (index >= 0) this.db.complianceFiles[index] = file;
    else this.db.complianceFiles.push(file);
    return { value: this.legajoOf(person) };
  }

  private legajoOf(person: Person): ComplianceFileView {
    const file = this.db.complianceFiles.find(f => f.personOid === person.oid);
    return {
      personOid: person.oid, personName: person.displayName, documentType: person.documentType, documentNumber: person.documentNumber,
      file: file ? {
        state: file.state, updateDate: file.updateDate, activity: file.activity, fundsOrigin: file.fundsOrigin, pep: file.pep,
        pepRole: file.pepRole, obligedSubjectDeclared: file.obligedSubjectDeclared, attachments: [...file.attachments],
      } : null,
    };
  }

  // ── P-87: unusual operations ──────────────────────────────────

  createUnusual(body: UnusualReportBody): Outcome<UnusualReport> {
    const me = this.me();
    if (!me || !this.isProducer()) return failure(403, 'FORBIDDEN', 'Los reportes de operaciones inusuales son del productor');
    const checked = this.validateUnusual(body);
    if ('error' in checked) return checked;
    const today = this.host.today();
    const report: UnusualReport = {
      oid: `unu-${this.host.nextSequence()}`, ownerOid: me.oid, ...checked.value, status: 'draft',
      createDate: today, updateDate: today, filedDate: null, receiptNumber: null,
    };
    this.db.unusualReports.unshift(report);
    return { value: report };
  }

  updateUnusual(oid: string, body: UnusualReportBody): Outcome<UnusualReport> {
    const report = this.unusual(oid);
    if ('error' in report) return report;
    if (report.value.status === 'filed') return failure(409, 'REPORT_FILED', 'Un reporte presentado no se edita');
    const checked = this.validateUnusual(body);
    if ('error' in checked) return checked;
    Object.assign(report.value, checked.value, { updateDate: this.host.today() });
    return report;
  }

  prepareUnusual(oid: string): Outcome<UnusualReport> {
    const report = this.unusual(oid);
    if ('error' in report) return report;
    if (report.value.status !== 'draft') return failure(409, 'REPORT_STATE', 'Solo un borrador se puede preparar');
    report.value.status = 'prepared';
    report.value.updateDate = this.host.today();
    return report;
  }

  fileUnusual(oid: string, filedDate: string, receiptNumber: string): Outcome<UnusualReport> {
    const report = this.unusual(oid);
    if ('error' in report) return report;
    if (report.value.status !== 'prepared') return failure(409, 'REPORT_STATE', 'Primero hay que preparar el reporte');
    if (!receiptNumber.trim()) return failure(400, 'REPORT_INVALID', 'Falta el número de constancia');
    const today = this.host.today();
    if (!filedDate || filedDate > today) return failure(400, 'REPORT_INVALID', 'La fecha de presentación no puede ser futura');
    report.value.status = 'filed';
    report.value.filedDate = filedDate;
    report.value.receiptNumber = receiptNumber.trim();
    report.value.updateDate = today;
    return report;
  }

  private unusual(oid: string): Outcome<UnusualReport> {
    const report = this.db.unusualReports.find(r => r.oid === oid);
    if (!this.isProducer() || !report || !this.inScope(report.ownerOid)) return failure(404, 'NOT_FOUND', 'Reporte inexistente');
    return { value: report };
  }

  private validateUnusual(body: UnusualReportBody): Outcome<Pick<UnusualReport, 'personOid' | 'policyOids' | 'operationDate' | 'description' | 'attachments'>> {
    const person = this.person(body.personOid);
    if (!person) return failure(404, 'NOT_FOUND', 'Elegí un cliente de tu árbol');
    if (!body.description?.trim()) return failure(400, 'REPORT_INVALID', 'Describí qué tiene de inusual la operación');
    if (!body.operationDate || body.operationDate > this.host.today()) {
      return failure(400, 'REPORT_INVALID', 'La fecha de la operación es obligatoria y no puede ser futura');
    }
    const policyOids = [...new Set(body.policyOids ?? [])];
    const policies = policyOids.map(oid => this.db.policies.find(p => p.oid === oid));
    if (policies.some(p => !p || p.personOid !== person.oid || !this.inScope(p.ownerOid))) {
      return failure(400, 'REPORT_INVALID', 'Las pólizas deben ser del cliente elegido');
    }
    return {
      value: {
        personOid: person.oid, policyOids, operationDate: body.operationDate, description: body.description.trim(),
        attachments: [...(body.attachments ?? [])],
      },
    };
  }

  // ── P-86: emission book (the one cross-tree query, D-019) ─────

  private ownNodeOids(): Set<string> {
    const user = this.host.user();
    const memberships = this.host.session.support ? [] : MEMBERSHIPS[this.host.session.username ?? ''] ?? [];
    const nodes = memberships.map(m => m.memberOid);
    return new Set(nodes.length > 0 ? nodes : user?.memberOid ? [user.memberOid] : []);
  }

  emissionBook(from: string, to: string): Outcome<EmissionBookView> {
    if (!this.isProducer()) return failure(403, 'FORBIDDEN', 'El libro de emisión es del productor');
    if (!from || !to || from > to) return failure(400, 'PERIOD_INVALID', 'Elegí un período válido');
    return { value: this.bookView(from, to) };
  }

  generateEmissionBook(from: string, to: string): Outcome<EmissionBookView> {
    if (!this.isProducer()) return failure(403, 'FORBIDDEN', 'El libro de emisión es del productor');
    if (!from || !to || from > to) return failure(400, 'PERIOD_INVALID', 'Elegí un período válido');
    const rows = emissionOperations(this.db, this.ownNodeOids(), from, to);
    const entry: EmissionBookEntry = {
      oid: `book-${this.host.nextSequence()}`, username: this.host.session.username ?? '', from, to, generatedDate: this.host.today(),
      count: rows.length, premiumTotal: rows.reduce((sum, r) => sum + r.premium, 0), format: 'PDF',
    };
    this.db.emissionBooks.unshift(entry);
    return { value: this.bookView(from, to) };
  }

  private bookView(from: string, to: string): EmissionBookView {
    const nodes = this.ownNodeOids();
    const operations = emissionOperations(this.db, nodes, from, to);
    const treeOids = new Set(this.db.members.filter(m => nodes.has(m.oid)).map(m => m.treeOid));
    const username = this.host.session.username ?? '';
    return {
      from, to, operations, totalPremium: operations.reduce((sum, r) => sum + r.premium, 0),
      trees: this.db.trees.filter(t => treeOids.has(t.oid)).map(t => t.name), crossTree: treeOids.size > 1,
      generated: this.db.emissionBooks.filter(b => b.username === username)
        .map(b => ({ oid: b.oid, from: b.from, to: b.to, generatedDate: b.generatedDate, count: b.count, premiumTotal: b.premiumTotal, format: b.format })),
    };
  }

  // ── Policy detail extras (P-41) ───────────────────────────────

  policyExtras(oid: string): Outcome<PolicyExtras> {
    const policy = this.db.policies.find(p => p.oid === oid);
    if (!policy || !this.inScope(policy.ownerOid)) return failure(404, 'NOT_FOUND', 'Póliza inexistente');
    return { value: policyExtras(policy, this.db) };
  }

  // ── Drafts (P-31, P-61) ───────────────────────────────────────

  saveDraft(body: DraftBody): Outcome<DraftRow> {
    const me = this.me();
    if (!me) return failure(403, 'FORBIDDEN', 'Sin sesión');
    if (body.kind !== 'quote' && body.kind !== 'claim') return failure(400, 'DRAFT_INVALID', 'Tipo de borrador inválido');
    const today = this.host.today();
    const existing = body.oid ? this.db.drafts.find(d => d.oid === body.oid && d.ownerOid === me.oid) : undefined;
    const fields = {
      kind: body.kind, personOid: body.personOid ?? null, label: body.label.slice(0, 140), step: Math.max(0, Math.floor(body.step)),
      updateDate: today, data: structuredClone(body.data ?? {}),
    };
    if (existing) Object.assign(existing, fields);
    const draft: WizardDraft = existing ?? { oid: `drf-${this.host.nextSequence()}`, ownerOid: me.oid, createDate: today, ...fields };
    if (!existing) this.db.drafts.unshift(draft);
    return { value: this.draftRow(draft) };
  }

  drafts(kind: DraftKind | null): DraftRow[] {
    const me = this.me();
    return this.db.drafts.filter(d => d.ownerOid === me?.oid && (!kind || d.kind === kind)).map(d => this.draftRow(d));
  }

  draft(oid: string): Outcome<DraftRow> {
    const found = this.db.drafts.find(d => d.oid === oid && d.ownerOid === this.me()?.oid);
    return found ? { value: this.draftRow(found) } : failure(404, 'NOT_FOUND', 'Borrador inexistente');
  }

  deleteDraft(oid: string): Outcome<{ deleted: true }> {
    const index = this.db.drafts.findIndex(d => d.oid === oid && d.ownerOid === this.me()?.oid);
    if (index < 0) return failure(404, 'NOT_FOUND', 'Borrador inexistente');
    this.db.drafts.splice(index, 1);
    return { value: { deleted: true } };
  }

  /** A submitted wizard consumes its draft; a draft that never reaches submission creates nothing else. */
  consumeDraft(oid: string | undefined, kind: DraftKind): void {
    if (!oid) return;
    const index = this.db.drafts.findIndex(d => d.oid === oid && d.kind === kind && d.ownerOid === this.me()?.oid);
    if (index >= 0) this.db.drafts.splice(index, 1);
  }

  private draftRow(draft: WizardDraft): DraftRow {
    return {
      oid: draft.oid, kind: draft.kind, label: draft.label, step: draft.step, updateDate: draft.updateDate,
      personOid: draft.personOid, data: structuredClone(draft.data),
    };
  }
}
